import { AG_GRID_LOCALE_EN, AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { AppConfig, GridNullFlag } from '@app/core/app.config';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { isAssetRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Check } from '@app/core/model/entities/asset/check';
import { Actor, Entity, IRelatedEntity, RelatedEntity } from '@app/core/model/entities/entity';
import { FieldConfig, FormatType } from '@app/core/model/other/field-config';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AggregationGridComponent } from '@app/shared/components/aggregation-grid/aggregation-grid.component';
import {
  DateTimeCellEditComponent
} from '@app/shared/components/cell-edit/date-time-cell-edit/date-time-cell-edit.component';
import {
  FileNameCellEditComponent
} from '@app/shared/components/cell-edit/file-name-cell-edit/file-name-cell-edit.component';
import { NumberCellEditComponent } from '@app/shared/components/cell-edit/number-cell-edit/number-cell-edit.component';
import {
  CustomTextSuffixCellEditorParams
} from '@app/shared/components/cell-edit/text-suffix-cell-edit/text-suffix-cell-edit.component';
import {
  CheckResult,
  CheckResultCellComponent
} from '@app/shared/components/cell-renderer/check-result-cell/check-result-cell.component';
import {
  CustomCellRendererComponent
} from '@app/shared/components/cell-renderer/custom-cell-renderer/custom-cell-renderer.component';
import {
  LinkCellRendererComponent
} from '@app/shared/components/cell-renderer/link-cell-renderer/link-cell-renderer.component';
import {
  PictogramCellRendererComponent
} from '@app/shared/components/cell-renderer/pictogram-cell-renderer/pictogram-cell-renderer.component';
import { Dimensions } from '@app/shared/extra/column-dimensions.enum';
import { ColumnType } from '@app/shared/extra/column-type.enum';
import { ExcelType } from '@app/shared/extra/excel-type.enum';
import {
  customCssBuilderForEntity,
  customCssBuilderForValue,
  getCurrencySymbol,
  getLocale,
  getValue,
  isNullOrEmpty,
  setValue,
  translateValueBuilder
} from '@app/shared/extra/utils';
import { FieldFormatTypePipe } from '@app/shared/pipes/field-format-type.pipe';
import { ValidationService } from '@app/shared/services/validation.service';
import { TranslateService } from '@ngx-translate/core';
import compareTo from '@private/dayjs-compareto';
import { ExternalLinkService } from '@services/external-link.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import {
  CellClassParams,
  CellEditorSelectorResult,
  CellKeyDownEvent,
  CellRendererSelectorResult,
  CellValueChangedEvent,
  ColDef,
  ICellEditorParams,
  ICellRendererParams,
  IDateFilterParams,
  KeyCreatorParams,
  ProcessCellForExportParams,
  SuppressKeyboardEventParams,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterFunc,
  ValueGetterParams,
  ValueSetterParams
} from 'ag-grid-community';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { distinctUntilChanged, first, map, skipWhile } from 'rxjs/operators';
import { MaterialTooltipComponent } from '../components/material-tooltip/material-tooltip.component';

/**
 * Metadata carried on ColDef.context.
 */
export interface ColDefContext<TData = any, TValue = any> {
  formatType?: FormatType;
  suffix?: string;
  translate?: boolean;
  fieldConfig?: FieldConfig;
  assetTypeIds?: string[];
  noFilter?: boolean;
  validate?: (event: CellValueChangedEvent, onSuccess: () => void, onFailure: () => void) => void;
  onPaste?: (params: ProcessCellForExportParams<TData, TValue>) => any;
  onCopy?: (params: ProcessCellForExportParams<TData, TValue>) => any;
  onKeyDownDelete?: (event: CellKeyDownEvent<TData, TValue>) => void;
  editModeEnabled?: boolean;
  fullRowEdit?: boolean;
}

@Injectable()
export class ColumnBuilder<TData = any> {
  private readonly translate = inject(TranslateService);
  private readonly appConfig = inject(AppConfig);
  private readonly fieldFormatPipe = inject(FieldFormatTypePipe);
  private readonly appManager = inject(AppManager);
  private readonly validationService = inject(ValidationService);
  private readonly accessManager = inject(AccessManager);
  private readonly externalLinkService = inject(ExternalLinkService);

  /**
   * Lower-cases values before the Set Filter's mini-filter (search box) compares them, so the search box stays
   * case-insensitive even though `caseSensitive: true` makes the filter list and row matching case-sensitive.
   * @param value the raw filter list value or mini-filter search text to format
   * @return the value converted to lower case, or the original value if it isn't a string
   */
  private readonly setFilterMiniFilterTextFormatter = (value: string): string => typeof value === 'string' ? value.toLowerCase() : value;

  constructor() {
    dayjs.extend(compareTo);
    dayjs.extend(customParseFormat);
  }

  /**
   * Creates a string column definition for AG-Grid with full support for display, filtering, sorting, editing, and paste operations.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the cell value from row data.
   * @param options Optional column configuration including translation, suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with string-specific behaviors including:
   *  - Automatic value translation if `translate` option is true
   *  - Text suffix appending if `suffix` option is provided
   *  - Set filter with custom value formatting
   *  - Text cell editor with optional autocomplete suggestions from field values
   *  - String-with-number comparator for natural sorting (e.g., "Item 2" before "Item 10")
   *  - Paste suppression if UNIQUE validator is present
   *  - Whitespace trimming and newline replacement in value parser
   */
  public createStringColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, string>,
    options: Partial<ColDef<TData> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, string> {
    const valueFormatter: ValueFormatterFunc<TData, string> = params => {
      if (this.isGridValueInvalid(params.value?.toString())) {
        return this.appConfig.GRID_EMPTY_VALUE;
      }

      if (!!options.context?.translate) {
        // Translate the value directly
        return this.translateValue(params.value.toUpperCase(), options.colId) + options.context?.suffix;
      } else {
        return params.value + options.context?.suffix;
      }
    };

    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        if (value === this.appConfig.GRID_NULL_FLAG) return value;

        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          return value;
        }
        return undefined;
      },
      valueFormatter: valueFormatter,
      // filtering
      suppressFiltersToolPanel: !!options.context?.noFilter,
      menuTabs: (!options.context?.noFilter && ['filterMenuTab'] || []).concat('generalMenuTab'), // Always keep generalMenuTab, but only add filter if allowed
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agSetColumnFilter',
      filterParams: {
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        valueFormatter: valueFormatter,
        cellRendererSelector: (params: ICellRendererParams<TData>): CellRendererSelectorResult => {
          if (!params || [this.appConfig.GRID_EMPTY_VALUE, this.translate.instant('GRID.SELECT_ALL')].includes(params.valueFormatted)) {
            return undefined;
          }

          return this.setFilterRendererSelector(true, options.context?.fieldConfig, params);
        }
      },
      cellRendererSelector: (params): CellRendererSelectorResult => {
        if (!params || [this.appConfig.GRID_EMPTY_VALUE, this.translate.instant('GRID.SELECT_ALL')].includes(params.valueFormatted)) {
          return undefined;
        }

        return this.cellRendererSelector(false, options.context?.fieldConfig, params);
      },
      // sorting
      comparator: (valueA: string, valueB: string): number => {
        if (!!options.context?.translate) {
          return this.stringWithNumberComparator(
            this.translateValue(valueA, options.colId) + options.context?.suffix,
            this.translateValue(valueB, options.colId) + options.context?.suffix
          );
        } else {
          return this.stringWithNumberComparator(valueA, valueB);
        }
      },
      // editing
      cellEditor: 'textCellEditor',
      cellEditorParams: (params: ICellEditorParams<TData>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          value: params.formatValue(params.value),
          // Text fields can accept autocompletes
          suggestedOptions: options.context?.fieldConfig?.field?.fieldValues
        };
      },
      equals: (valueA: any, valueB: any): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        if (!!options.context?.translate) {
          return this.stringWithNumberComparator(
            this.translateValue(valueA, options.colId) + options.context?.suffix,
            this.translateValue(valueB, options.colId) + options.context?.suffix
          ) === 0;
        } else {
          return this.stringWithNumberComparator(valueA, valueB) === 0;
        }
      },
      valueParser: params => {
        // Trim whitespaces before setting the value
        return params.newValue.trim().replaceAll('\\n', ' ');
      },
      // pasting
      // FIXME Enable isValueTaken validation with paste
      // disable pasting if the column has isValueTaken validator
      suppressPaste: params => !params.node?.data || !!options.context?.fieldConfig?.field?.validators.find(validator => {
        return validator.type === ValidatorType.UNIQUE;
      }),
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // pasting: no specific processing for strings
        onPaste: params => params.value,
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a file name column definition for AG-Grid with full support for display, filtering, sorting, editing, and paste operations.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the cell value from row data.
   * @param options Optional column configuration including translation, suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with string-specific behaviors including:
   *  - Automatic value translation if `translate` option is true
   *  - Text suffix appending if `suffix` option is provided
   *  - Set filter with custom value formatting
   *  - Text cell editor with optional autocomplete suggestions from field values
   *  - String-with-number comparator for natural sorting (e.g., "Item 2" before "Item 10")
   *  - Paste suppression if UNIQUE validator is present
   *  - Whitespace trimming and newline replacement in value parser
   */
  public createFileNameColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, string>,
    options: Partial<ColDef<TData> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, string> {
    return {
      ...this.createStringColumn(header, valueGetter, options),
      cellEditor: FileNameCellEditComponent
    };
  }

  /**
   * Creates a specialized column for displaying check results with support for DPE (energy performance) checks.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve a Check result from row data.
   * @param options Optional column configuration including field config and AG-Grid ColDef properties.
   * @returns Extended column definition with check result-specific behaviors including:
   *  - Custom cell renderer (CheckResultCellComponent) for visual representation
   *  - Special handling for DPE checks: combines climate and power labels (e.g., "A-1")
   *  - Special handling for 'observation' field: displays observation data by check type
   *  - Set filter with custom value formatting
   *  - String-with-number comparator for sorting formatted check results
   */
  public createCheckResultColumn(
    header: string,
    valueGetter: ((params: ValueGetterParams) => any),
    options: Partial<ColDef<TData> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, CheckResult> {
    const result = (value: CheckResult): string => {
      if (value?.observation) {
        return value?.observation;
      } else if (value?.climateLabel || value?.powerLabel) {
        return value?.climateLabel + '-' + value?.powerLabel;
      } else if (value?.result) {
        return value?.result;
      } else {
        return undefined;
      }
    };

    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: (params: ValueGetterParams<Check, CheckResult>) => {
        const subValue = valueGetter(params);
        if (this.isGridValueInvalid(subValue)) { // Empty string, undefined or even __NULL__ itself
          return this.appConfig.GRID_NULL_FLAG;
        }
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (!this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          && !this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) return '';

        const check = params.data;

        if (options.context?.fieldConfig?.fieldCode === 'observation' && !!check?.computedProperties['observation']) {
          return {observation: check?.computedProperties?.['observation']};

        } else if (options.context?.fieldConfig?.fieldCode === 'result' && !!check?.computedProperties['result']) {
          if (check?.type === 'dpe' && check?.properties['powerLabel'] && check?.properties['climateLabel']) {
            return {climateLabel: check?.properties['climateLabel'], powerLabel: check?.properties['powerLabel']};
          } else {
            return {result: check?.computedProperties['result']};
          }
        }

        return undefined;
      },
      // Used in Excel export and copy, do not remove
      valueFormatter: (params: ValueFormatterParams<Check, Record<string, string> | GridNullFlag | undefined>) => {
        if (!params.value || params.value === this.appConfig.GRID_NULL_FLAG) return this.appConfig.GRID_EMPTY_VALUE;
        return Object.values(params.value).join(' ');
      },
      cellRenderer: CheckResultCellComponent,
      useValueFormatterForExport: true,
      // filtering
      suppressFiltersToolPanel: !!options.context?.noFilter,
      menuTabs: (!options.context?.noFilter && ['filterMenuTab'] || []).concat('generalMenuTab'), // Always keep generalMenuTab, but only add filter if allowed
      filter: 'agSetColumnFilter',
      filterParams: {
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        valueFormatter: (params: ValueFormatterParams<TData, string>) => {
          return this.isGridValueInvalid(params.value) ? this.appConfig.GRID_EMPTY_VALUE : params.value;
        },
        cellRendererSelector: (params: ICellRendererParams<TData>): CellRendererSelectorResult => {
          if (params && ![this.appConfig.GRID_EMPTY_VALUE, this.translate.instant('GRID.SELECT_ALL')].includes(params.valueFormatted)) {
            return this.setFilterRendererSelector(true, options.context?.fieldConfig, params);
          }
        }
      },
      // sorting
      comparator: (valueA, valueB): number => {
        const checkA = result(valueA);
        const checkB = result(valueB);

        return this.stringWithNumberComparator(checkA, checkB);
      },
      equals: (valueA, valueB): boolean => {
        const checkA = result(valueA);
        const checkB = result(valueB);
        if (!checkA && !checkB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        return this.stringWithNumberComparator(checkA, checkB) === 0;
      },
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // copying
        onCopy: params => params.formatValue(params.value),
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a column for displaying actor entities with their names.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve an Actor object from row data.
   * @param options Optional column configuration including field config and AG-Grid ColDef properties.
   * @returns Extended column definition with actor-specific behaviors including:
   *  - Displays actor.name property as the formatted value
   *  - Set filter with actorId as the key for filtering by actor identity
   *  - String-with-number comparator for sorting by actor names
   */
  public createActorColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, Actor>,
    options: Partial<ColDef<TData, Actor> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, Actor> {
    const valueFormatter: ValueFormatterFunc<TData, Actor> = params => {
      // If value is not undefined, format it properly, otherwise return GRID_EMPTY_VALUE
      if (params.value) {
        return params.value.name;
      } else {
        return this.appConfig.GRID_EMPTY_VALUE;
      }
    };
    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      keyCreator: params => params.value.name,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          return valueGetter(params);
        }
        return null;
      },
      valueFormatter: valueFormatter,
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agSetColumnFilter',
      filterParams: {
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        valueFormatter: valueFormatter,
        comparator: (valueA: string, valueB: string) => this.stringWithNumberComparator(valueA, valueB),
        keyCreator: params => params.value.actorId
      },
      // sorting
      comparator: (valueA: string, valueB: string) => this.stringWithNumberComparator(valueA, valueB),
      equals: (valueA: string, valueB: string) => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }
        return this.stringWithNumberComparator(valueA, valueB) === 0;
      },
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a column for list/dropdown fields with predefined values and optional translation support.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the selected list value from row data.
   * @param options Optional column configuration including translation, suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with list-specific behaviors including:
   *  - Translation using column ID prefix if `translate` option is true (e.g., "VALUE.{colId}_{value}")
   *  - Select cell editor with predefined options from field values, formatted with translations
   *  - Value formatted for both display and editing (shows translated text, stores code value)
   *  - Set filter with translated value formatting
   *  - Smart paste handling: attempts to match pasted translated text back to original list code
   *  - Copy operation exports translated values when translation is enabled
   *  - Supports both string and number comparison for sorting
   */
  public createListColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, number | string>,
    options: Partial<ColDef<TData, number | string> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, number | string> {
    const valueFormatter: ValueFormatterFunc<TData, string> = params => {
      if (this.isGridValueInvalid(params.value?.toString())) {
        return this.appConfig.GRID_EMPTY_VALUE;
      }

      if (!!options.context?.translate) {
        // Translate the value with the column id as a grouping factor
        return this.translate.instant(`VALUE.${options.colId}_${params.value}`.toUpperCase()) + options.context?.suffix;
      } else {
        return params.value + options.context?.suffix;
      }
    };

    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        if (value === this.appConfig.GRID_NULL_FLAG) return value;
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          return value;
        }
        return undefined;
      },
      valueFormatter: valueFormatter,
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agSetColumnFilter',
      filterParams: {
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        valueFormatter: valueFormatter,
        cellRendererSelector: (params: ICellRendererParams<TData>): CellRendererSelectorResult => {
          if (!params || [this.appConfig.GRID_EMPTY_VALUE, this.translate.instant('GRID.SELECT_ALL')].includes(params.valueFormatted)) {
            return undefined;
          }

          return this.setFilterRendererSelector(true, options.context?.fieldConfig, params);
        }
      },
      // sorting
      comparator: (valueA, valueB): number => {
        if (typeof valueA === 'number' || typeof valueB === 'number') {
          return this.numberComparator(valueA, valueB);
        } else if (!!options.context?.translate) {
          return this.stringWithNumberComparator(
            this.translateValue(valueA, options.colId) + options.context?.suffix,
            this.translateValue(valueB, options.colId) + options.context?.suffix
          );
        } else {
          return this.stringWithNumberComparator(valueA, valueB);
        }
      },
      cellRendererSelector: (params): CellRendererSelectorResult => {
        if (!params || [this.appConfig.GRID_EMPTY_VALUE, this.translate.instant('GRID.SELECT_ALL')].includes(params.valueFormatted)) {
          return undefined;
        }

        return this.cellRendererSelector(false, options.context?.fieldConfig, params);
      },
      // editing
      cellEditor: 'selectCellEditor',
      cellEditorParams: (params: ICellEditorParams<TData>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          // List is a special case where the selected value must be formatted first
          value: {
            entityId: params.value,
            entityName: params.formatValue(params.value)
          },
          // We send all the possible values as a combination of value/translation
          selectOptions: options.context?.fieldConfig?.field?.fieldValues?.map((fieldValue) => ({
            label: params.formatValue(fieldValue),
            optionValue: fieldValue
          }))
        };
      },
      equals: (valueA, valueB): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        if (!!options.context?.translate) {
          return this.stringWithNumberComparator(
            this.translateValue(valueA?.toString(), options.colId) + options.context?.suffix,
            this.translateValue(valueB?.toString(), options.colId) + options.context?.suffix
          ) === 0;
        } else {
          return this.stringWithNumberComparator(valueA?.toString(), valueB?.toString()) === 0;
        }
      },
      valueParser: params => {
        // Trim whitespaces before setting the value
        return params.newValue.trim().replaceAll('\\n', ' ');
      },
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // copying: copy the translated value from the list if available instead of its code
        onCopy: params => {
          return options.context?.fieldConfig?.customOptions?.translate
            ? this.translateValue(params.value, options.context?.fieldConfig?.code)
            : params.value;
        },
        // pasting: assuming a translated value is pasted, find if it is present among other translated values
        onPaste: params => {
          const formattedValue = params.value.trim().replaceAll('\\n', ' ');
          const listValues = [].concat(options.context?.fieldConfig?.field?.fieldValues);
          const index = listValues
            .map(fieldValue => this.translateValue(fieldValue, options.context?.fieldConfig?.code))
            .indexOf(formattedValue);

          if (params.value == '') {
            return params.value;
          }
          return index >= 0 ? // Check if the value is found in the list
            listValues[index] :
            getValue(params.node.data, options.context?.fieldConfig?.fieldPath); // reject the new value, set the old one
        },
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a multi-line text area column with support for newlines and extended content display.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the text content from row data.
   * @param options Optional column configuration including translation, suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with textarea-specific behaviors including:
   *  - Material tooltip cell renderer for displaying long text with ellipsis
   *  - Text area cell editor for multi-line editing
   *  - Ellipsis cell class for truncating overflowing text
   *  - Text column filter (agTextColumnFilter) for flexible searching
   *  - Preserves newline characters: replaces "\\n" with actual newlines in parser
   *  - Special copy behavior: escapes newlines to "\\n" in edit mode to prevent cell separator issues
   *  - Suppressed keyboard events: arrow keys, Shift+Enter, and Delete in edit mode remain in cell
   */
  public createTextAreaColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, string>,
    options: Partial<ColDef<TData, string> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, string> {
    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, ['ellipsis', ExcelType.TEXT]),
      width: Dimensions.XXL,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        if (value === this.appConfig.GRID_NULL_FLAG) return value;
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          return value;
        }
        return undefined;
      },
      valueFormatter: params => {
        if (this.isGridValueInvalid(params.value?.toString())) {
          return this.appConfig.GRID_EMPTY_VALUE;
        }
        if (!!options.context?.translate) {
          // Translate the value directly
          return this.translate.instant(`VALUE.${params.value}`.toUpperCase()) + options.context?.suffix;
        } else {
          return params.value + options.context?.suffix;
        }
      },
      cellRenderer: MaterialTooltipComponent,
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agTextColumnFilter',
      // sorting
      comparator: this.comparator.bind(this),
      // editing
      cellEditor: 'textAreaEditor',
      cellEditorParams: (params: ICellEditorParams<TData, string>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          value: params.value
        };
      },
      equals: (valueA, valueB): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        return this.comparator(valueA, valueB) === 0;
      },
      valueParser: params => {
        // Trim whitespaces before setting the value
        return params.newValue.trim().replaceAll('\\n', '\n');
      },
      // Add common option for all type of column.
      ...common,
      // other
      suppressKeyboardEvent: params => {
        const keyCode = params.event.key;
        const context = params.colDef.context as ColDefContext;
        return keyCode === 'ArrowLeft' ||
          keyCode === 'ArrowUp' ||
          keyCode === 'ArrowDown' ||
          keyCode === 'ArrowRight' ||
          (params.event.shiftKey && keyCode === 'Enter') ||
          (context?.fullRowEdit && keyCode === 'Enter') ||
          (keyCode === 'Delete' && context?.editModeEnabled);
      },
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // copy: in edit mode, allow copying the escape character so that line returns within the text-area cells
        // are not considered a cell value separator.
        onCopy: params => {
          return params.column.getColDef().editable ? params.value?.replaceAll('\n', '\\n') : params.value;
        },
        // pasting: strip wrapping quotes from pasted text
        onPaste: params => {
          return params.value?.replaceAll(/^"|"$/g, '');
        },
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a numerical column with formatting, validation, and aggregation support.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the numeric value from row data.
   * @param options Optional column configuration including format type (percentage, currency, etc.), suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with numerical-specific behaviors including:
   *  - Custom formatting via FieldFormatTypePipe supporting currency, percentage, and other numeric formats
   *  - AggregationGridComponent cell renderer for displaying aggregated values with custom CSS
   *  - Number column filter (agNumberColumnFilter) for range-based filtering
   *  - Number cell editor (NumberCellEditComponent) with validator support
   *  - Numeric comparator for proper number sorting
   *  - Chart data type defaulted to 'series' for charting capabilities
   *  - Paste validation: rejects non-numeric values and restores original value on invalid paste
   *  - Zero values are treated as valid and displayed (not treated as empty)
   */
  public createNumericalColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, number | string>,
    options: Partial<ColDef<TData, number | string> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, number | string> {
    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.NUMERICAL,
      cellClass: params => {
        const additionalClass = options.context?.formatType ? options.context.formatType : ExcelType.NUMERICAL;
        return this.getCellClass(params, options, ColumnType.NUMERICAL, [additionalClass]);
      },
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        if (value === this.appConfig.GRID_NULL_FLAG) return value;
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          return value as number;
        }
        return undefined;
      },
      valueFormatter: params => {
        if ([this.appConfig.GRID_NULL_FLAG, '', undefined, 0].includes(params.value?.toString()) || Number.isNaN(params.value)) {
          return this.appConfig.GRID_EMPTY_VALUE;
        }

        return (
          this.fieldFormatPipe.transform(
            params.value,
            options.context?.formatType,
            this.appManager.currentOrganization.currency
          ) + options.context?.suffix
        );
      },
      cellRenderer: AggregationGridComponent,
      cellRendererParams: (params: ICellRendererParams<TData, number | string>): any => {
        // If the current row is a group, use one of its children none grouped rows instead
        const rowData = params.node.group ? params.node.allLeafChildren?.first()?.data : params.data ?? params.data;
        return {
          styles: params.valueFormatted !== this.appConfig.GRID_EMPTY_VALUE && options.context?.fieldConfig && rowData instanceof Entity
            ? customCssBuilderForEntity(options.context?.fieldConfig.customCss, rowData) : {},
          column: params.column,
          valueFormatted: params.valueFormatted
        };
      },
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agNumberColumnFilter',
      // sorting
      comparator: this.numberComparator.bind(this),
      // editing
      cellEditor: NumberCellEditComponent,
      cellEditorParams: (params: ICellEditorParams<TData, number | string>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          value: valueGetter({node: params.node, data: params.data, colDef: params.colDef} as ValueGetterParams)
        };
      },
      equals: (valueA, valueB): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        return this.numberComparator(valueA, valueB) === 0;
      },
      valueParser: params => {
        // Cast to Number
        return Number(params.newValue);
      },
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // pasting: verify that the pasted value is a number
        onPaste: params => {
          return (!Number.isNaN(params.value) || params.value == '')
            ? params.value
            : getValue(params.node.data, options.context?.fieldConfig?.fieldPath); // reject the new value, set the old one
        },
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a boolean column displaying Yes/No values with select-based editing.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the boolean value (or string 'true'/'false') from row data.
   * @param options Optional column configuration including field config and AG-Grid ColDef properties.
   * @returns Extended column definition with boolean-specific behaviors including:
   *  - Value formatter converts true/'true' to translated "Yes" and false/'false' to translated "No"
   *  - Set column filter (agSetColumnFilter) with Yes/No options
   *  - Select cell editor with Yes/No dropdown options
   *  - Value parser casts to Boolean type
   *  - Paste validation: accepts 'true', 'false', or empty string via toBooleanString() method
   */
  public createBooleanColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, boolean | string>,
    options: Partial<ColDef<TData, boolean | string> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, boolean | string> {
    const valueFormatter: ValueFormatterFunc<TData, boolean | string> = params => {
      switch (params.value) {
        case true:
        case 'true':
          return this.translate.instant('LABEL.YES');
        case false:
        case 'false':
          return this.translate.instant('LABEL.NO');
        default:
          return this.appConfig.GRID_EMPTY_VALUE;
      }
    };

    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.BOOLEAN,
      cellClass: params => this.getCellClass(params, options, ColumnType.BOOLEAN, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        if (value === this.appConfig.GRID_NULL_FLAG) return value;

        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          return value as boolean;
        }
        return undefined;
      },
      valueFormatter: valueFormatter,
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agSetColumnFilter',
      filterParams: {
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        valueFormatter: valueFormatter
      },
      // sorting
      comparator: this.comparator.bind(this),
      // editing
      cellEditor: 'selectCellEditor',
      cellEditorParams: (params: ICellEditorParams<TData, boolean | string>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          value: {
            entityId: params.value,
            entityName: params.formatValue(params.value)
          },
          selectOptions: [
            {optionValue: 'true', label: this.translate.instant('LABEL.YES')},
            {optionValue: 'false', label: this.translate.instant('LABEL.NO')}
          ]
        };
      },
      equals: (valueA: any, valueB: any): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        return this.comparator(valueA, valueB) === 0;
      },
      valueParser: params => {
        // Cast to Boolean
        return Boolean(params.newValue);
      },
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // pasting: verify that the pasted value is a boolean
        onPaste: params => {
          const newValue = params.value.toBooleanString();
          return (newValue === 'true' || newValue === 'false' || newValue == '')
            ? params.value
            : getValue(params.node.data, options.context?.fieldConfig?.fieldPath); // reject the new value, set the old one
        },
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a date or datetime column with dayjs-based parsing, formatting, and filtering.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the date string from row data.
   * @param options Optional column configuration including field config (with fieldType DATE or DATETIME) and AG-Grid ColDef properties.
   * @returns Extended column definition with date-specific behaviors including:
   *  - Format detection: uses DISPLAY_DATETIME_FORMAT for DATETIME fields, DISPLAY_DATE_FORMAT for DATE fields
   *  - UTC conversion for DATETIME values: converts to 'YYYY-MM-DDTHH:mm:ss[Z]' format in valueGetter
   *  - Custom date formatter via appConfig.getFormattedDate() respecting field type
   *  - Date column filter (agDateColumnFilter) with dayjs comparator for date comparisons
   *  - Cell editor selector: DateTimeCellEditComponent for DATETIME, dateCellEditor for DATE
   *  - Date comparator using dayjs with invalid date handling
   *  - Chart data type defaulted to 'time' for time-series charting
   *  - Paste validation: accepts valid dayjs-parseable dates or empty string
   *  - Value parser formats to display format, ignoring empty values
   */
  public createDateColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, string>,
    options: Partial<ColDef & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, string> {
    const fieldType = options.context?.fieldConfig?.field?.fieldType;
    const displayDate =
      FieldTypeEnum.DATETIME === fieldType ? this.appConfig.DISPLAY_DATETIME_FORMAT : this.appConfig.DISPLAY_DATE_FORMAT;
    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.DATE,
      cellClass: params => this.getCellClass(params, options, ColumnType.DATE, [ExcelType.DATE]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        if (value === this.appConfig.GRID_NULL_FLAG) return value;
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          if (value && fieldType === FieldTypeEnum.DATETIME && dayjs(value).isValid()) {
            return dayjs(value).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
          }
          return value;
        }
        return undefined;
      },
      valueFormatter: params => {
        if (this.isGridValueInvalid(params.value?.toString())) {
          return this.appConfig.GRID_EMPTY_VALUE;
        }
        return this.appConfig.getFormattedDate(params.value, options.context?.fieldConfig?.field?.fieldType);
      },
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agDateColumnFilter',
      filterParams: <IDateFilterParams>{
        comparator: (filterDateAtMidnight, cellValue): number => {
          if (cellValue !== this.appConfig.GRID_EMPTY_VALUE) {
            return dayjs(cellValue).compareTo(dayjs(filterDateAtMidnight));
          } else {
            return undefined;
          }
        }
        // FIXME TTT-4210 This parameter adds a timepicker in the filter, absent from Ag-Grid v29.x
        // includeTime: FieldTypeEnum.DATETIME === options.context?.fieldConfig?.field?.fieldType
      },
      // sorting
      comparator: this.dateComparator.bind(this),
      // editing
      cellEditorSelector: (): CellEditorSelectorResult => {
        if (FieldTypeEnum.DATETIME === fieldType) {
          return <CellEditorSelectorResult>{
            component: DateTimeCellEditComponent
          };
        } else {
          return <CellEditorSelectorResult>{
            component: 'dateCellEditor'
          };
        }
      },
      cellEditorParams: (params: ICellEditorParams<TData, string>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          value: params.value
        };
      },
      equals: (valueA: any, valueB: any): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        return this.dateComparator(valueA, valueB) === 0;
      },
      valueParser: params => {
        // Cast to Date, ignore empty values as Dayjs returns the current date if the value is empty
        return params.newValue !== '' ? dayjs(params.newValue).format(displayDate) : params.newValue;
      },
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // pasting: verify that the pasted value is a date compatible with Dayjs
        onPaste: params => {
          return dayjs(params.value).isValid() || params.value === ''
            ? params.value
            : getValue(params.node.data, options.context?.fieldConfig?.fieldPath); // reject the new value, set the old one
        },
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a column for displaying multiple values as a comma-separated list with chip-based editing.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve an array of values from row data.
   * @param options Optional column configuration including translation, suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with multi-value-specific behaviors including:
   *  - Value formatter joins array elements with ", " separator, translating each if enabled
   *  - Set column filter (agSetColumnFilter) with separate filterValueGetter returning GRID_EMPTY_VALUE for empty arrays
   *  - Cell editor selector: multipleEntityAutocompleteCellEditor for Entity instances, multipleAutocompleteCellEditor otherwise
   *  - Autocomplete support from field values in cell editor
   *  - Array comparator: joins arrays before comparing as strings
   *  - Paste handling: splits comma-separated values, trims, removes duplicates and empty strings
   *  - Value parser: maps array elements to trim whitespace and replace newlines
   *  - Suppressed keyboard events: all keys while editing, Delete when edit mode enabled
   */
  public createMultiValueColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, any[] | GridNullFlag>,
    options: Partial<ColDef<TData, any[] | GridNullFlag> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, any[] | GridNullFlag> {
    const valueFormatter: ValueFormatterFunc<TData, any[] | GridNullFlag> = params => {
      if (this.isGridValueInvalid(params.value?.toString())) {
        return this.appConfig.GRID_EMPTY_VALUE;
      }

      // If value is not GRID_EMPTY_VALUE, format it properly, otherwise return GRID_EMPTY_VALUE
      if (Array.isArray(params.value)) {
        if (params.value.length === 0) return this.appConfig.GRID_EMPTY_VALUE;
        if (!!options.context?.translate) {
          // Translate the value with the column id as a grouping factor
          // Two lines to fix https://github.com/biesbjerg/ngx-translate-extract/issues/250
          const values = params.value.map((item) => this.translateValue(item, options.colId) + options.context?.suffix);
          return values?.join(', ');
        } else {
          return params.value?.map((item) => item + options.context?.suffix)?.join(', ');
        }
      } else {
        return params.value;
      }
    };

    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        if (value === this.appConfig.GRID_NULL_FLAG) return value;
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          // return undefined if undefined
          if (Array.isArray(value) && value.length > 0) {
            return value.map(it => it.toString());
          }
          // GRID_EMPTY_VALUE, if undefined or empty array
          return [];
        }
        return undefined;
      },
      valueFormatter: valueFormatter,
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agSetColumnFilter',
      filterParams: {
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        valueFormatter: valueFormatter,
        cellRendererSelector: (params: ICellRendererParams<TData, any[] | GridNullFlag>): CellRendererSelectorResult => {
          if (!params || [this.appConfig.GRID_EMPTY_VALUE, this.translate.instant('GRID.SELECT_ALL')].includes(params.valueFormatted)) {
            return undefined;
          }

          return this.setFilterRendererSelector(true, options.context?.fieldConfig, params);
        },
        getValue: (params: ValueGetterParams<TData, any[] | GridNullFlag>): any => {
          // execute valueGetter
          if (!(params.data instanceof Entity) || this.validationService.validateConditions(
            params.data,
            options.context?.fieldConfig?.conditionsToView
          )) {
            const colDef = {context: {fieldConfig: options.context?.fieldConfig}};
            const value = valueGetter({...params, colDef} as typeof params);
            if (Array.isArray(value) && value.length > 0) {
              return value.map(it => it.toString());
            }
            // GRID_EMPTY_VALUE, if undefined or empty array
            return this.appConfig.GRID_EMPTY_VALUE;
          } else {
            return undefined;
          }
        }
      },
      // sorting
      sortable: false,
      comparator: (valueA: any[] | string, valueB: any[] | string): number => {
        if (Array.isArray(valueA)) {
          valueA = valueA.join();
        }
        if (Array.isArray(valueB)) {
          valueB = valueB.join();
        }
        return this.stringWithNumberComparator(valueA, valueB);
      },
      // editing
      cellEditorSelector: (params: ICellEditorParams<TData, any[] | GridNullFlag>): CellEditorSelectorResult => {
        if (params.value instanceof Entity) {
          return <CellEditorSelectorResult>{
            component: 'multipleEntityAutocompleteCellEditor'
          };
        } else {
          return <CellEditorSelectorResult>{
            component: 'multipleAutocompleteCellEditor'
          };
        }
      },
      cellEditorParams: (params: ICellEditorParams<TData, any[] | GridNullFlag>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          value: params.value,
          // Chip fields can accept autocompletes
          suggestedOptions: options.context?.fieldConfig?.field?.fieldValues
        };
      },
      equals: (valueA: any[] | string, valueB: any[] | string): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        if (Array.isArray(valueA)) {
          valueA = valueA.join();
        }
        if (Array.isArray(valueB)) {
          valueB = valueB.join();
        }
        return this.stringWithNumberComparator(valueA, valueB) === 0;
      },
      valueParser: params => {
        // Assuming the new value is an array, trim the values
        return (Array.isArray(params.newValue) ? params.newValue : [])
          .map((val: string) => val.trim().replaceAll('\\n', ' '));
      },
      // Add common option for all type of column.
      ...common,
      // other
      suppressKeyboardEvent: params => {
        const keyCode = params.event.key;
        const context = params.colDef.context as ColDefContext;
        return params.editing || (keyCode === 'Delete' && context?.editModeEnabled);
      },

      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // pasting: assuming the pasted data is a string comma separated values, split them and return them
        onPaste: params => {
          const arr = params.value.split(',').map((val: string) => val.trim().replaceAll('\\n', ' '));
          // Remove duplicates
          return arr.filter((elem: string, index: number) => arr.indexOf(elem) === index && elem !== '');
        },
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a hyperlink column that opens URLs in a new browser tab when clicked.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve the URL string from row data.
   * @param options Optional column configuration including field config and AG-Grid ColDef properties.
   * @returns Extended column definition with link-specific behaviors including:
   *  - LinkCellRendererComponent for displaying clickable link with styling
   *  - Text column filter (agTextColumnFilter) for URL filtering
   *  - onCellClicked handler opens URL in new tab with 'noopener,noreferrer' for security
   *  - Sortable enabled by default
   */
  public createLinkColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, string>,
    options: Partial<ColDef<TData, string> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, string> {
    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView)) {
          return valueGetter(params);
        }
        return undefined;
      },
      cellRenderer: LinkCellRendererComponent,
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agTextColumnFilter',
      // sorting
      sortable: true,
      onCellClicked: (params): void => {
        // params.context here is GridOptions.context (the grid-level context, e.g. the component instance),
        // not ColDef.context.
        this.externalLinkService.confirmExternalLink(params.value)
          .pipe(takeUntilDestroyed(params.context.destroyRef))
          .subscribe();
      },
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a column for displaying multiple related entities (e.g. assets, projects) as a comma-separated list.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve an array of IRelatedEntity objects from row data.
   * @param options Optional column configuration including translation, suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with entities-specific behaviors including:
   *  - Value formatter joins entity displayNames with ", " separator, optionally translating each
   *  - Server-side row model (SSRM) compatibility with special grouped row handling
   *  - Set column filter (agSetColumnFilter) with entity display name formatting
   *  - Separate filterValueGetter converts entities to strings for filtering
   *  - Multiple entity autocomplete cell editor (multipleEntityAutocompleteCellEditor) for editing
   *  - Autocomplete support from field values in cell editor
   *  - Array comparator: joins entity arrays before comparing as strings
   *  - Paste handling: splits comma-separated values, trims, removes duplicates
   *  - Suppressed keyboard events: all keys while editing, Delete when edit mode enabled
   */
  public createEntitiesColumn(
    header: string,
    valueGetter: ((params: ValueGetterParams<TData, IRelatedEntity[] | GridNullFlag>) => (IRelatedEntity[] | GridNullFlag)),
    options: Partial<ColDef<TData, IRelatedEntity[] | GridNullFlag> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, IRelatedEntity[] | GridNullFlag> {
    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: params => {
        // execute valueGetter
        const value = valueGetter(params);
        // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
        // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
        const isValid = this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
          || !(params.data instanceof Entity)
          || this.validationService.validateConditions(params.data, options.context?.fieldConfig?.conditionsToView);

        if (value === this.appConfig.GRID_NULL_FLAG || isValid && Array.isArray(value)) {
          return value;
        }

        return undefined;
      },
      valueFormatter: params => {
        const isNonEmptyRelatedEntityList = (value: typeof params.value): value is IRelatedEntity[] => {
          return ![this.appConfig.GRID_NULL_FLAG, '', undefined].includes(value?.toString())
            && Array.isArray(value)
            && value.length !== 0;
        };
        if (!isNonEmptyRelatedEntityList(params.value)) return this.appConfig.GRID_EMPTY_VALUE;

        const suffix = options.context?.translate ? options.context?.suffix : '';
        // Translate the value with the column id as a grouping factor
        // Two lines to fix https://github.com/biesbjerg/ngx-translate-extract/issues/250
        return params.value
          .map((entity: IRelatedEntity) => this.translateValue(entity.displayName, options.colId) + suffix)
          .join(', ');
      },
      keyCreator: params => {
        if (!params.value || params.value === this.appConfig.GRID_NULL_FLAG) return params.value;
        return params.value.map(it => new RelatedEntity(it).toString());
      },
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agSetColumnFilter',
      filterParams: {
        defaultOption: 'contains',
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        keyCreator: (params: KeyCreatorParams<TData, IRelatedEntity>) => params.value?.displayName ?? params.value,
        valueFormatter: (params: ValueFormatterParams<TData, IRelatedEntity | string>): string => {
          if (typeof params.value === 'string') {
            return params.value;
          } else {
            return params.value?.displayName || this.appConfig.GRID_EMPTY_VALUE;
          }
        },
        cellRendererSelector: (params: ICellRendererParams<TData>): CellRendererSelectorResult => {
          if (!params || [this.appConfig.GRID_EMPTY_VALUE, this.translate.instant('GRID.SELECT_ALL')].includes(params.valueFormatted)) {
            return undefined;
          }

          return this.setFilterRendererSelector(true, options.context?.fieldConfig, params);
        },
        getValue: (params: ValueGetterParams<TData, IRelatedEntity[] | GridNullFlag>): string[] | string => {
          // execute valueGetter
          if (!(params.data instanceof Entity) || this.validationService.validateConditions(
            params.data,
            options.context?.fieldConfig?.conditionsToView
          )) {
            const colDef = {context: {fieldConfig: options.context?.fieldConfig}};
            const value = valueGetter({...params, colDef} as typeof params);
            if (Array.isArray(value) && value.length > 0) {
              return value.map(it => new RelatedEntity(it).toString());
            }
            // GRID_EMPTY_VALUE, if undefined or empty array
            return this.appConfig.GRID_EMPTY_VALUE;
          } else {
            return undefined;
          }
        }
      },
      // sorting
      sortable: [FieldTypeEnum.ASSET, FieldTypeEnum.MEETING].includes(options.context?.fieldConfig?.field?.fieldType),
      comparator: (valueA: IRelatedEntity[] | string, valueB: IRelatedEntity[] | string): number => {
        if (Array.isArray(valueA)) {
          valueA = valueA.map(it => new RelatedEntity(it).toString()).join();
        }
        if (Array.isArray(valueB)) {
          valueB = valueB.map(it => new RelatedEntity(it).toString()).join();
        }
        return this.stringWithNumberComparator(valueA, valueB);
      },
      // editing
      cellEditorSelector: (): CellEditorSelectorResult => {
        return <CellEditorSelectorResult>{
          component: 'multipleEntityAutocompleteCellEditor'
        };
      },
      cellEditorParams: (params: ICellEditorParams<TData, IRelatedEntity[] | GridNullFlag>): CustomTextSuffixCellEditorParams => {
        return {
          fieldConfig: options.context?.fieldConfig,
          validators: options.context?.fieldConfig?.field?.validators,
          value: params.value,
          // Chip fields can accept autocompletes
          suggestedOptions: options.context?.fieldConfig?.field?.fieldValues
        };
      },
      equals: (valueA: IRelatedEntity[] | string, valueB: IRelatedEntity[] | string): boolean => {
        if (!valueA && !valueB) {
          // If both values are different kinds of falsy, they are presumed to be equal
          return true;
        }

        if (Array.isArray(valueA)) {
          valueA = valueA.join();
        }
        if (Array.isArray(valueB)) {
          valueB = valueB.join();
        }
        return this.stringWithNumberComparator(valueA, valueB) === 0;
      },
      valueParser: (params): string[] => {
        // Assuming the new value is an array, trim the values
        return (Array.isArray(params.newValue) ? params.newValue : [])
          .map((val: string) => val.trim().replaceAll('\\n', ' '));
      },
      // Add common option for all type of column.
      ...common,
      // other
      suppressKeyboardEvent: (params): boolean => {
        const keyCode = params.event.key;
        const context = params.colDef.context as ColDefContext;
        return params.editing || (keyCode === 'Delete' && context?.editModeEnabled);
      },

      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // copying
        onCopy: (params): string => params.value?.map(it => it['displayName']).join(','),
        // pasting: assuming the pasted data is a string comma separated values, split them and return them
        onPaste: (params): string[] => {
          const arr = params.value.split(',').map((val: string) => val.trim().replaceAll('\\n', ' '));
          // Remove duplicates
          return arr.filter((elem: string, index: number) => arr.indexOf(elem) === index && elem !== '');
        },
        ...common.context,
        ...options.context
      }
    };
  }

  /**
   * Creates a column for displaying a single related entity (e.g., asset, meeting) as a formatted string.
   * @param header Column header text to be displayed (translatable key).
   * @param valueGetter Function to retrieve an array of IRelatedEntity object from row data.
   * @param options Optional column configuration including translation, suffix, field config, and AG-Grid ColDef properties.
   * @returns Extended column definition with entity-specific behaviours.
   */
  public createEntityColumn(
    header: string,
    valueGetter: ValueGetterFunc<TData, IRelatedEntity>,
    options: Partial<ColDef<TData, IRelatedEntity> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData, IRelatedEntity> {
    const subValueGetter: ValueGetterFunc<TData, IRelatedEntity> = params => {
      // In SSRM group rows, bypass conditions referencing other fields — those fields are empty in the
      // synthetic group entity and would incorrectly hide the value. See shouldBypassGroupRowConditions.
      if (this.shouldBypassGroupRowConditions(params, options.context?.fieldConfig)
        || params.data instanceof Entity && this.validationService.validateConditions(
          params.data,
          options.context?.fieldConfig?.conditionsToView
        )) {
        return valueGetter(params);
      }

      return void 0;
    };
    const common = this.commonColDef(options);
    return {
      headerName: header,
      headerClass: ColumnType.TEXT,
      cellClass: params => this.getCellClass(params, options, ColumnType.TEXT, [ExcelType.TEXT]),
      width: Dimensions.LG,
      // displaying
      valueGetter: subValueGetter,
      valueFormatter: params => {
        return params.value?.displayName?.let(value => value !== this.appConfig.GRID_NULL_FLAG ? value : this.appConfig.GRID_EMPTY_VALUE)
          ?? this.appConfig.GRID_EMPTY_VALUE;
      },
      keyCreator: (params: KeyCreatorParams<TData, IRelatedEntity>) => params.value?.displayName ?? params.value,
      // filtering
      filter: options.context?.fieldConfig?.customOptions?.['filter'] || 'agSetColumnFilter',
      filterParams: {
        defaultOption: 'contains',
        caseSensitive: true,
        textFormatter: this.setFilterMiniFilterTextFormatter,
        keyCreator: (params: KeyCreatorParams<Entity, IRelatedEntity>): string => {
          return params.value?.displayName ?? this.appConfig.GRID_EMPTY_VALUE;
        },
        valueFormatter: (params: ValueFormatterParams<Entity, IRelatedEntity>): string => {
          return params.value?.displayName ?? this.appConfig.GRID_EMPTY_VALUE;
        }
      },
      // sorting
      comparator: (valueA, valueB): number => {
        return this.stringWithNumberComparator(valueA?.displayName, valueB?.displayName);
      },
      // editing
      // TODO entity editing
      // pasting
      // TODO entity pasting
      // Add common option for all type of column.
      ...common,
      // Add all defined options at the end to override any previously set properties
      ...options,
      context: {
        // copying
        onCopy: (params): string => params.value?.displayName,
        ...common.context,
        ...options.context
      }
    };

  }

  public createColumn(
    inputType: FieldTypeEnum,
    header = '',
    valueGetter: ValueGetterFunc<TData>,
    options: Partial<ColDef<TData> & {context?: ColDefContext}> = {hide: false}
  ): ColDef<TData> {
    const currency = this.appManager.currentOrganization?.currency ? getCurrencySymbol(this.appManager.currentOrganization.currency) : '';
    options.context = {
      ...options.context,
      suffix: options.context?.suffix
        ? this.translate.instant('SUFFIX.' + options.context.suffix.toUpperCase(), {currency})
        : ''
    };

    switch (inputType) {
      case FieldTypeEnum.TEXT:
      case FieldTypeEnum.TEXT_AUTOCOMPLETE:
      case FieldTypeEnum.TEXT_HEADER:
        return this.createStringColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.FILE_NAME_HEADER:
        return this.createFileNameColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.CHECK_RESULT:
        return this.createCheckResultColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.ACTOR:
        return this.createActorColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.TEXTAREA:
        return this.createTextAreaColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.NUMERIC:
        return this.createNumericalColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.YEAR:
        // TODO TTT-3458 Remove adding the year validator
        options.context?.fieldConfig?.field?.validators.push(new FieldValidator(ValidatorType.YEAR, ''));
        return this.createNumericalColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.RADIOBUTTON:
        return this.createBooleanColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.DATE:
      case FieldTypeEnum.DATETIME:
        return this.createDateColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.LIST:
      case FieldTypeEnum.NUMERIC_LIST:
        return this.createListColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.CHIPS:
      case FieldTypeEnum.CHIPS_AUTOCOMPLETE:
      case FieldTypeEnum.LIST_MULTIVALUE:
        return this.createMultiValueColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.PROJECTS:
      case FieldTypeEnum.EQUIPMENTS:
      case FieldTypeEnum.SPACES:
      case FieldTypeEnum.DOCUMENT_RELATED_ASSETS:
      case FieldTypeEnum.ASSETS:
      case FieldTypeEnum.ASSET_GROUPS:
        return this.createEntitiesColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.ASSET:
      case FieldTypeEnum.MEETING:
        return this.createEntityColumn(this.translate.instant(header), valueGetter, options);
      case FieldTypeEnum.LINK:
        return this.createLinkColumn(this.translate.instant(header), valueGetter, options);
      default:
        return this.createStringColumn(this.translate.instant(header), valueGetter, options);
    }
  }

  // Grouped column definition, apply on grouped column
  public createGroupColumnDef(header: string): ColDef {
    return {
      headerName: header,
      menuTabs: ['generalMenuTab'],
      minWidth: Dimensions.XL,
      suppressMovable: true
    };
  }

  public dateComparator(valueA, valueB): number {
    const dateA = dayjs(valueA);
    const dateB = dayjs(valueB);
    if (dateA.isValid() && !dateB.isValid()) {
      return 1;
    } else if (dateB.isValid() && !dateA.isValid()) {
      return -1;
    } else if (dateA.isValid() && dateB.isValid()) {
      return dateA.compareTo(dateB);
    }
  }

  public comparator(valueA, valueB): number {
    if (valueA && !valueB) {
      return 1;
    } else if (valueB && !valueA) {
      return -1;
    } else if (valueA && valueB) {
      return valueA.localeCompare ? valueA.localeCompare(valueB) : 0;
    }
  }

  /**
   * Compare two strings that can have common number inside them using the first part of the string and then the number if present.
   * @param valueA
   * @param valueB
   */
  public stringWithNumberComparator(valueA: string, valueB: string): number {
    if (isNullOrEmpty(valueB) && isNullOrEmpty(valueA)) {
      return 0;
    } else if (!isNullOrEmpty(valueA) && isNullOrEmpty(valueB)) {
      return 1;
    } else if (!isNullOrEmpty(valueB) && isNullOrEmpty(valueA)) {
      return -1;
    } else {
      const nameArrayA = valueA.toString().match(/\D+|\d+/g);
      const nameArrayB = valueB.toString().match(/\D+|\d+/g);
      let index = 0;
      let result = 0;
      while (result === 0 && index < nameArrayA.length && index < nameArrayB.length) {
        if (typeof Number.parseInt(nameArrayA[index], 0) === 'number' && typeof Number.parseInt(
            nameArrayB[index],
            0
          ) === 'number'
          && !Number.isNaN(Number.parseInt(nameArrayA[index], 0)) && !Number.isNaN(Number.parseInt(
            nameArrayB[index],
            0
          ))) {
          result = this.numberComparator(Number.parseInt(nameArrayA[index], 0), Number.parseInt(nameArrayB[index], 0));
        } else {
          result = nameArrayA[index].localeCompare(nameArrayB[index]);
        }
        index++;
      }
      if (result === 0 && nameArrayA.length !== nameArrayB.length) {
        return nameArrayA.length - nameArrayB.length;
      } else {
        return result;
      }
    }
  }

  /**
   * Get locale text to use in a grid, depending on the browser's locale.
   * Locale 'fr' is used by default if the browser's locale is not supported.
   * @return A map of key/value pairs for localising text within a grid.
   */
  public localeTextCommon(): { [key: string]: string; } {
    if (getLocale().startsWith('en')) {
      return AG_GRID_LOCALE_EN;
    }
    return AG_GRID_LOCALE_FR;
  }

  /**
   * Translate the value if there is a key in the po files. Otherwise, return the value.
   * @param value
   * @param code the field's code
   */
  private translateValue(value: string, code: string): string {
    return translateValueBuilder(this.translate)(value, code);
  }

  /**
   * Get the CSS classes for a cell.
   * @param params The parameters for the cell class containing the data of the entity.
   * @param options The column definition and other options containing the field config.
   * @param columnType The type of the column to add as a class of th cell.
   * @param additionalCLasses Additional CSS classes to be added (default is undefined).
   * @returns An array of strings representing the CSS classes for the cell to added.
   * @private
   */
  private getCellClass(params: CellClassParams,
                       options: Partial<ColDef & {context?: ColDefContext}>,
                       columnType: ColumnType,
                       additionalCLasses: string[] = undefined): string[] {
    const cellClasses: string[] = [columnType];
    const entity = params.data;

    const hasFieldWriteAccess = options.context?.fieldConfig && this.accessManager.hasFieldWriteAccess(
      options.context?.fieldConfig.code,
      options.context?.fieldConfig.fieldEntityType
    );
    const isFieldEditable = this.validationService.isFieldEditable(options.context?.fieldConfig, entity);
    const validAssetTypes = options.context?.assetTypeIds?.length === 0 ||
      (isAssetRelatedEntity(entity) && entity.relatedAssets.some(({typeId}) => options.context?.assetTypeIds.includes(typeId)));

    if (!hasFieldWriteAccess || !isFieldEditable || !validAssetTypes) {
      cellClasses.push('readonly-column');
    }
    return [...cellClasses, ...additionalCLasses];
  };

  /**
   * Create a fragment of a column definition used by all type of column.
   * @param options containing the column definition and options of the column.
   * @return Fragment of a column definition
   * @private
   */
  private commonColDef(options: Partial<ColDef & {context?: ColDefContext}>): any {
    return {
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams): boolean => {
        const keyCode = params.event.key;
        const context = params.colDef.context as ColDefContext;
        // Suppress the keyboard event if the user press enter on full row edit
        if (context?.fullRowEdit && keyCode === 'Enter') {
          return true;
        }

        // If the user did not press delete or editable is undefined do not suppress the keyboard event.
        if (keyCode !== 'Delete' || !params.colDef.editable) {
          return false;
        }
        // Check if the column is editable
        return params.colDef.editable instanceof Function ? params.colDef.editable(params) : params.colDef.editable;
      },
      valueSetter: (params: ValueSetterParams): boolean => {
        if (!params.node.group || params.api.getGridOption('treeData')) {
          /**
           * ColDef Path contains the trace from the entity root to the value to set
           * Ex: path ['name'] => { name: 'newValue' }
           * Ex: path ['properties', 'assetType'] =>
           *     {
           *        properties: {
           *           assetType: 'newValue'
           *        }
           *     }
           */
          const {fieldConfig} = params.colDef.context as ColDefContext;
          setValue(params.data, fieldConfig.fieldPath, params.newValue);
          return true;
        } else {
          return false;
        }
      },
      context: {
        onKeyDownDelete: (event: CellKeyDownEvent): void => {
          if (!!options.context?.fieldConfig?.field?.computed) return;

          const newValue = '';
          event.node.setDataValue(event.column, newValue, 'keyDownDelete');
        },
        validate: (params: CellValueChangedEvent, onSuccess: () => void, onFailure: () => void): void => {
          const {fieldConfig} = params.colDef.context as ColDefContext;
          const control = new FormControl(
            {value: null},
            Validators.compose(
              fieldConfig.field.validators?.map((validator: FieldValidator) => this.validationService.getValidator(
                validator,
                params.node.data
              ))
            ),
            Validators.composeAsync(
              fieldConfig.field.validators?.map((validator: FieldValidator) => this.validationService.getAsyncValidator(
                validator,
                params.node.data,
                fieldConfig.fieldPath
              ))
            )
          );

          // Wait for async validation
          control.statusChanges.pipe(
            distinctUntilChanged(),
            skipWhile((status) => status === 'PENDING'),
            map((value) => {
              return value === 'VALID';
            }),
            first()
          ).subscribe(valid => {
            // Trigger the proper callback based on the control validity
            if (valid) {
              onSuccess ? onSuccess() : void 0;
            } else {
              onFailure ? onFailure() : void 0;
            }
          });

          control.setValue(params.newValue);
        }
      }
    };
  }

  private numberComparator(valueA, valueB): number {
    if (valueA === this.appConfig.GRID_EMPTY_VALUE || valueA === void 0) {
      valueA = Number.MIN_SAFE_INTEGER;
    }
    if (valueB === this.appConfig.GRID_EMPTY_VALUE || valueB === void 0) {
      valueB = Number.MIN_SAFE_INTEGER;
    }

    valueA = Number(valueA);
    valueB = Number(valueB);

    if (valueA && !valueB) {
      return 1;
    } else if (valueB && !valueA) {
      return -1;
    } else if (valueA && valueB) {
      return valueA.compareTo(valueB);
    }
  }

  /**
   * Select a cell renderer by leveraging the existence of pictograms or css classes in the fieldConfig
   * @param withText If not specified in fieldConfig, whether the pictogram should have a text of explanation.
   * @param fieldConfig Field config of the current row.
   * @param params
   */
  private cellRendererSelector(withText: boolean,
                               fieldConfig: FieldConfig,
                               params: ICellRendererParams): CellRendererSelectorResult {
    if (fieldConfig?.hasPictograms) {
      return {
        component: PictogramCellRendererComponent,
        params: {
          values: fieldConfig.pictograms,
          withText: fieldConfig.isPictogramWithText || withText,
          group: params.node?.group
        }
      };
    }

    if (fieldConfig?.customCss) {
      // If current row is a group, use one of its children none grouped rows instead
      const rowData = params.node?.group ? params.node.allLeafChildren?.first()?.data : params.data;

      return {
        component: CustomCellRendererComponent,
        params: {
          styles: (![this.appConfig.GRID_EMPTY_VALUE].includes(params.valueFormatted)) ? customCssBuilderForEntity(
            fieldConfig.customCss,
            rowData ?? params.data
          ) : {}
        }
      };
    }

    return void 0;
  }

  /**
   * Select a SetFilter renderer by leveraging the existence of pictograms or css classes in the fieldConfig
   * @param withText If not specified in fieldConfig, whether the pictogram should have a text of explanation.
   * @param fieldConfig Field config of the current row.
   * @param params Cell renderer params for the set filter
   */
  private setFilterRendererSelector(withText: boolean,
                                    fieldConfig: FieldConfig,
                                    params: ICellRendererParams): CellRendererSelectorResult {
    if (fieldConfig?.hasPictograms) {
      return {
        component: PictogramCellRendererComponent,
        params: {
          values: fieldConfig.pictograms,
          withText: fieldConfig.isPictogramWithText || withText,
          group: params.node?.group
        }
      };
    }

    if (fieldConfig?.customCss) {
      return {
        component: CustomCellRendererComponent,
        params: {
          styles: ![this.appConfig.GRID_EMPTY_VALUE].includes(params.valueFormatted) ? customCssBuilderForValue(
            fieldConfig.customCss,
            params.value
          ) : {}
        }
      };
    }

    return void 0;
  }

  /**
   * Determines whether conditionsToView evaluation should be bypassed for an SSRM group row.
   *
   * In SSRM, group rows are synthetic entities that only carry the group key field — all other fields are
   * empty. Evaluating a condition that references an empty field always returns false, which would
   * incorrectly hide values from users who are allowed to see them.
   *
   * We bypass only when at least one condition references a field other than this column's own field:
   * - Conditions on the current column's field can be evaluated correctly when the column is the group key.
   * - Conditions on other fields cannot be evaluated (empty in the group row), so we show the value.
   *
   * Limitation: if the column is NOT the group key but has a self-referential condition, the field will
   * still be empty and the condition will evaluate to false (hide). This is the safe/correct fallback.
   */
  private shouldBypassGroupRowConditions(params: ValueGetterParams, fieldConfig: FieldConfig): boolean {
    if (params.api.getGridOption('rowModelType') !== 'serverSide' || !params.node.group) {
      return false;
    }
    const currentFieldPath = fieldConfig?.fieldPath?.join('.');
    // Bypass if any condition targets a field other than the current column's own field,
    // since that field is absent in the group row entity and would incorrectly fail the condition.
    return fieldConfig?.conditionsToView?.some(c => c.field !== currentFieldPath) ?? false;
  }

  /**
   * Check if the Value of a cell is invalid
   * @param value the value of the grid cell
   * @return true if invalid, otherwise false
   */
  private isGridValueInvalid(value?: string): boolean {
    return value == null || value === '' || value === this.appConfig.GRID_NULL_FLAG;
  }
}
